import { computed, ref } from 'vue';
import type { Router } from 'vue-router';
import { formatDateForApi, parseCivilDate } from '@/shared/lib';
import { trainingApi, type CreateSgqPunctualTrainingPayload, type TrainingDetail } from '@/entities/Training';
import { useOnboardingMinistrantes, type OnboardingMinistranteRow } from './useOnboardingMinistrantes';
import { useSgqDocumentSelection } from './useSgqDocumentSelection';
import { useTrainingFormSubmit } from './useTrainingFormSubmit';
import { useTrainingSchedulingWithCollaborators } from './useTrainingSchedulingWithCollaborators';
import { buildMinistranteSessionPayload } from '../lib/buildMinistranteSessionPayload';

export interface UseSgqPunctualFormEditContext {
  trainingId: number;
  initial: TrainingDetail;
  /**
   * Captured synchronously by the edit dispatcher before its await, since this composable
   * is instantiated after one — useRouter() would silently resolve to undefined by then.
   */
  router?: Router;
}

function buildInitialMinistranteRows(initial: TrainingDetail): OnboardingMinistranteRow[] {
  return initial.instructors.map((instructor) => ({
    id: instructor.sessionId,
    responsibleMemberId: instructor.memberId,
    responsibleUserPositionId: instructor.position?.id ?? null,
    trainingDate: parseCivilDate(instructor.trainingDate),
  }));
}

export function useSgqPunctualForm(editContext?: UseSgqPunctualFormEditContext) {
  const { saving, submit: submitTraining } = useTrainingFormSubmit(editContext?.router);

  // SgqPunctualForm.vue also calls load() from its own onMounted (needed for the create
  // flow's load trigger), but in edit mode useEditTraining.ts's dispatcher already awaited
  // load() once before that component mounts. Without this flag, the redundant call would
  // reapply editContext.initial and re-run initializeParticipants's network fetch for no
  // reason.
  let editContextApplied = false;

  const notes = ref('');
  const scheduledDate = ref<Date | null>(null);

  const {
    scheduledByMemberId,
    scheduledByPositionId,
    members,
    loading,
    collaborators,
    visibleMemberIds,
    scheduledByPositions,
    memberOptions,
    load: loadScheduling,
    setScheduledByMemberId,
    initializeParticipants,
  } = useTrainingSchedulingWithCollaborators();

  const ministrantes = useOnboardingMinistrantes(
    members,
    editContext ? buildInitialMinistranteRows(editContext.initial) : undefined
  );
  const documentSelection = useSgqDocumentSelection();

  function applyInitial(initial: TrainingDetail): void {
    notes.value = initial.notes ?? '';
    scheduledDate.value = parseCivilDate(initial.scheduling.date);
    scheduledByMemberId.value = initial.scheduling.responsible?.id ?? null;
    scheduledByPositionId.value = initial.scheduling.position?.id ?? null;

    const firstDocument = initial.documents[0];
    documentSelection.selectedDocument.value = firstDocument
      ? {
          documentRevisionId: firstDocument.documentRevisionId,
          prefix: firstDocument.prefix ?? '',
          title: firstDocument.title ?? '',
          revision: firstDocument.revision ?? 0,
        }
      : null;
  }

  async function load(): Promise<void> {
    const applyEditContext = editContext && !editContextApplied ? editContext : null;
    if (applyEditContext) {
      editContextApplied = true;
      applyInitial(applyEditContext.initial);
    }
    await loadScheduling();
    if (applyEditContext) {
      await initializeParticipants(
        applyEditContext.initial.positions.map((position) => position.id),
        applyEditContext.initial.trainees.map((trainee) => trainee.memberId)
      );
    }
  }

  const canSave = computed(() => {
    return (
      scheduledByMemberId.value !== null &&
      scheduledByPositionId.value !== null &&
      scheduledDate.value !== null &&
      documentSelection.selectedDocument.value !== null &&
      visibleMemberIds.value.length > 0 &&
      ministrantes.isComplete.value
    );
  });

  function buildSubmitPayload(): CreateSgqPunctualTrainingPayload | null {
    if (
      scheduledByMemberId.value === null ||
      scheduledByPositionId.value === null ||
      scheduledDate.value === null ||
      documentSelection.selectedDocument.value === null ||
      visibleMemberIds.value.length === 0
    ) {
      return null;
    }

    const apiScheduledDate = formatDateForApi(scheduledDate.value);
    if (apiScheduledDate === null) return null;

    const sessions: CreateSgqPunctualTrainingPayload['sessions'] = [];
    for (const row of ministrantes.rows.value) {
      const session = buildMinistranteSessionPayload(row);
      if (session === null) return null;
      sessions.push(session);
    }

    const participants = visibleMemberIds.value.map(Number);
    if (participants.some((memberId) => Number.isNaN(memberId))) return null;

    const positionIds = collaborators.selectedPositions.value.map(Number);
    if (positionIds.some((positionId) => Number.isNaN(positionId))) return null;

    return {
      notes: notes.value.trim() ? notes.value.trim() : undefined,
      scheduledBy: { memberId: scheduledByMemberId.value, positionId: scheduledByPositionId.value },
      plannedDate: apiScheduledDate,
      sessions,
      documentRevisionId: documentSelection.selectedDocument.value.documentRevisionId,
      participants,
      positionIds,
    };
  }

  async function submit(onSuccess?: () => void): Promise<void> {
    if (editContext) {
      await submitTraining(
        canSave.value,
        buildSubmitPayload,
        (payload) => trainingApi.updateSgqPunctualTraining(editContext.trainingId, payload),
        { mode: 'edit', onSuccess }
      );
      return;
    }

    await submitTraining(canSave.value, buildSubmitPayload, (payload) =>
      trainingApi.createSgqPunctualTraining(payload)
    );
  }

  const snapshot = computed(() =>
    JSON.stringify({
      notes: notes.value.trim(),
      scheduledByMemberId: scheduledByMemberId.value,
      scheduledByPositionId: scheduledByPositionId.value,
      scheduledDate: scheduledDate.value ? formatDateForApi(scheduledDate.value) : null,
      documentRevisionId: documentSelection.selectedDocument.value?.documentRevisionId ?? null,
      participants: [...visibleMemberIds.value].sort(),
      positions: [...collaborators.selectedPositions.value].sort(),
      ministrantes: ministrantes.rows.value.map((row) => ({
        responsibleMemberId: row.responsibleMemberId,
        responsibleUserPositionId: row.responsibleUserPositionId,
        trainingDate: row.trainingDate ? formatDateForApi(row.trainingDate) : null,
      })),
    })
  );

  return {
    notes,
    scheduledByMemberId,
    scheduledByPositionId,
    scheduledDate,
    members,
    scheduledByPositions,
    memberOptions,
    loading,
    saving,
    canSave,
    ministrantes,
    documentSelection,
    collaborators,
    snapshot,
    load,
    setScheduledByMemberId,
    submit,
  };
}
